import type { DestinationNode, OrderService, ShippingChannel } from './orders.js';
import { decodeEntities } from './orders-wire.js';

/**
 * MYP-53 Slice 2 — reference-data adapters for the order creation/edit flow.
 *
 * Every function here was written against live responses captured through the vendor site itself
 * (`https://www.mypoz.com/OutterApi/Api/*`), not the vendor's PDF documentation or the Jira
 * ticket's own description of the endpoints — both were found to be wrong in ways that would have
 * produced an incorrect UI if trusted. This file went through two rounds of that live capture: the
 * first missed that `channels` and `country` were checked against reconstructed rows rather than a
 * fresh fetch, which shipped wrong field names for both; the second (below) is what a real fetch
 * actually returned, and is what this file now reads.
 *
 * - `channels` has no volumetric-divisor field at all. `ShippingChannel.volumetricDivisor` was an
 *   invented fixture value; the real wire only carries `calcweightway` (WEIGHT / VOL / BAG,
 *   resolved through the `WEIGHT_BASIS` dictionary) and free-text notice fields.
 * - `channels` also has no closed set of restriction codes, no `enname`/`channelname`/`notice`/
 *   `minday`/`maxday` fields, and no field literally called "notice". The real fields are `name`/
 *   `name_cn` (Chinese display name), `name_en`/`englishname` (English — identical to the Chinese
 *   name on 43 of 205 live channels that were never translated, so it is read with a fallback to
 *   the Chinese name rather than left blank), `remark`/`remark_en` (the free-text notice, Chinese
 *   truthy on 191/205 rows and English truthy on only 61/205 — the same fallback applies), and
 *   `transday1`/`transday2` (transit days — not `minday`/`maxday`, which do not exist on the wire
 *   and previously made every channel render "0–0 days").
 * - `country` inverts what its field names suggest: live rows show `name` holding the upper-case
 *   English label (e.g. `"MALAYSIA"`) and `enname` — despite its own name — holding the Chinese
 *   text (e.g. `"马来西亚"`). That is the opposite of `province`/`city`, where `name` is already the
 *   display string and `enname` is usually null, and it is the opposite of what an earlier pass at
 *   this file read (it had `name` as Chinese and `enname` as English), because that pass was
 *   written from a reconstructed sample rather than a live fetch. The English label is also
 *   title-cased here rather than rendered shouting, since the wire sends it all upper-case.
 * - `order_services` is keyed by warehouse id, and the Jira ticket's "11 services" is actually 11
 *   warehouse keys — each value is itself an array of service rows, not a single record, totaling
 *   28 distinct services. The keyed map itself (the envelope's `keyed`) is what this file reads
 *   since MYP-119, so each group's warehouse id is known — see `normaliseOrderServices`.
 * - `order_services` row fields are also not what an earlier pass assumed: there is no `minfee` or
 *   `needconfirm` field on the wire. The real fields are `name`/`name_cn` (Chinese), `name_en`/
 *   `englishname` (English), `remark`/`remark_cn` (Chinese notice), `remark_en` (English notice),
 *   and `confirm` (`"Y"`/`"N"`/`""` — read as `requiresConfirmation`). `fee` and `defaultfee`
 *   disagree on every one of the 28 live services; the vendor's answer (MYP-80) is that customers
 *   see `fee`, so `fee` is read and `defaultfee` never is. There being no separate "minimum fee"
 *   field either, that whole concept has been dropped from `OrderService` rather than left reading
 *   a field that isn't there.
 * - `city` *is* filterable by `provinceid` (`{ provinceid: '12' }` correctly returns only that
 *   province's rows), contrary to the ticket's claim that every parameter combination returns the
 *   same 534 rows. Provinces and cities are therefore fetched lazily per selected parent rather
 *   than pulled whole and filtered client-side.
 * - The registry's actual endpoint slugs are `channels`, `country`, `province`, `city` — not the
 *   `channel_list`/`country_list`/`province_list`/`city_list` shorthand the Jira card used.
 *   `catalog-api.ts` (in `apps/web`) calls the real slugs; this file only normalises rows, so it
 *   never names an endpoint itself.
 */

/** Which language column to read off a reference-data row. */
export type CatalogLanguage = 'zh' | 'en';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Reads a field as a trimmed string, or `undefined` if absent, not a string, or blank. */
function str(row: Record<string, unknown>, key: string): string | undefined {
  const value = row[key];
  if (typeof value !== 'string') return undefined;
  const trimmed = decodeEntities(value).trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * A price field as text, to be sent back exactly as the vendor wrote it: a string trimmed, a finite
 * number as its own digits. Anything else (blank, null, an object) is no price at all.
 */
function feeText(value: unknown): string | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : undefined;
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/** `fee` as cents, with its currency, or nothing for a blank or non-numeric fee. */
function feeFields(
  value: unknown,
  currency: string | undefined,
): { feeCents?: number; currency?: string } {
  const raw = feeText(value);
  const amount = raw === undefined ? NaN : Number(raw);
  if (!Number.isFinite(amount)) return {};
  return { feeCents: Math.round(amount * 100), ...(currency ? { currency } : {}) };
}

/** The first defined, non-blank string among several candidate fields, in order. */
function firstNonEmpty(row: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const value = str(row, key);
    if (value !== undefined) return value;
  }
  return undefined;
}

/**
 * A channel's free-text notice, cleaned up for display: entity-decoded (via `decodeEntities`),
 * `\r\n` normalised to `\n`, and blank lines collapsed. Tags are not stripped — the vendor's notice
 * text is plain prose, not markup, in every row seen live.
 */
function cleanNotice(raw: string | undefined): string | undefined {
  if (raw === undefined) return undefined;
  const normalised = raw
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')
    .join('\n');
  return normalised === '' ? undefined : normalised;
}

/** Upper/lower-cased vendor English ("MALAYSIA") to title case ("Malaysia"). */
function toTitleCase(value: string): string {
  return value
    .toLowerCase()
    .split(' ')
    .map((word) => (word.length === 0 ? word : word[0]!.toUpperCase() + word.slice(1)))
    .join(' ');
}

/**
 * `channel_list` rows → `ShippingChannel[]`.
 *
 * `language` picks which of the channel's paired name fields to read; the vendor list is not
 * pre-filtered by language, only labelled per row.
 */
export function normaliseChannels(
  rows: readonly unknown[],
  language: CatalogLanguage,
): readonly ShippingChannel[] {
  const channels: ShippingChannel[] = [];

  for (const raw of rows) {
    if (!isRecord(raw)) continue;

    const id = firstNonEmpty(raw, ['id']);
    const name =
      language === 'zh'
        ? firstNonEmpty(raw, ['name', 'name_cn'])
        : firstNonEmpty(raw, ['name_en', 'englishname', 'name', 'name_cn']);
    const weightBasis = firstNonEmpty(raw, ['calcweightway']);
    const transportType = firstNonEmpty(raw, ['transtype']);

    if (id === undefined || name === undefined || weightBasis === undefined) continue;

    const nameNative = language === 'en' ? firstNonEmpty(raw, ['name', 'name_cn']) : undefined;
    const transitDaysMin = Number(str(raw, 'transday1') ?? '0');
    const transitDaysMax = Number(str(raw, 'transday2') ?? '0');
    const notice =
      language === 'zh'
        ? firstNonEmpty(raw, ['remark', 'remark_cn'])
        : firstNonEmpty(raw, ['remark_en', 'remark', 'remark_cn']);

    channels.push({
      id,
      name,
      nameNative,
      transportType: transportType ?? '',
      weightBasis,
      notice: cleanNotice(notice),
      transitDaysMin: Number.isFinite(transitDaysMin) ? transitDaysMin : 0,
      transitDaysMax: Number.isFinite(transitDaysMax) ? transitDaysMax : 0,
    });
  }

  return channels;
}

/**
 * `order_services` → `OrderService[]`.
 *
 * The payload is keyed by warehouse id, each value an array of service rows (MYP-119). Pass the
 * keyed map itself (the envelope's `keyed`) so a row is read the same way `packages_services`'
 * rows are: its own `warehouseid` first, else the key it is listed under. A plain array of groups
 * is still accepted, for which only the row's own `warehouseid` is known.
 *
 * Services are kept per warehouse: the same id under two warehouses gives two entries, each with
 * its own warehouse and price, and `servicesForWarehouse` picks the right one. Only a true repeat
 * — same warehouse, same id — collapses.
 */
export function normaliseOrderServices(
  input: readonly unknown[] | Readonly<Record<string, unknown>>,
  language: CatalogLanguage,
): readonly OrderService[] {
  const groups: readonly (readonly [string | undefined, unknown])[] = Array.isArray(input)
    ? input.map((group) => [undefined, group] as const)
    : Object.entries(input);
  const byKey = new Map<string, OrderService>();

  for (const [groupKey, group] of groups) {
    const listedUnder =
      typeof groupKey === 'string' && groupKey.trim() !== '' ? groupKey : undefined;
    for (const raw of Array.isArray(group) ? group : [group]) {
      if (!isRecord(raw)) continue;

      const id = firstNonEmpty(raw, ['id']);
      const name =
        language === 'zh'
          ? firstNonEmpty(raw, ['name', 'name_cn'])
          : firstNonEmpty(raw, ['name_en', 'englishname', 'name', 'name_cn']);

      if (id === undefined || name === undefined) continue;

      // The row's own `warehouseid` first (live: always present, always equal to its key), else the
      // warehouse it is listed under. One entry per warehouse and service: an id offered by two
      // warehouses stays under both, each with its own price.
      const warehouseId = firstNonEmpty(raw, ['warehouseid']) ?? listedUnder;
      const key = `${warehouseId ?? ''}|${id}`;
      if (byKey.has(key)) continue;

      const nameNative = language === 'en' ? firstNonEmpty(raw, ['name', 'name_cn']) : undefined;
      const description =
        language === 'zh'
          ? firstNonEmpty(raw, ['remark', 'remark_cn'])
          : firstNonEmpty(raw, ['remark_en', 'remark', 'remark_cn']);
      const requiresConfirmation = str(raw, 'confirm') === 'Y';

      byKey.set(key, {
        id,
        warehouseId,
        name,
        nameNative,
        description: description ?? '',
        // The price is `fee`, never `defaultfee` (the vendor's answer to MYP-80): shown as
        // `feeCents`, and carried verbatim as `billingFee` for `orders_create`'s OrderServicesfee.
        ...feeFields(raw['fee'], str(raw, 'currency')),
        billingFee: feeText(raw['fee']),
        requiresConfirmation,
        ...(str(raw, 'img') !== undefined ? { imageUrl: str(raw, 'img') } : {}),
      });
    }
  }

  return Array.from(byKey.values());
}

/**
 * The order-status codes a package must carry to be eligible for a new order, from whatever
 * endpoint the ticket names as the source of truth for "orderable" packages. Rows are plain status
 * codes; nothing else on them is used.
 */
export function normaliseEligiblePackageStatusCodes(rows: readonly unknown[]): readonly string[] {
  const codes: string[] = [];

  for (const raw of rows) {
    if (!isRecord(raw)) continue;
    const code = firstNonEmpty(raw, ['code', 'status', 'statuscode']);
    if (code !== undefined) codes.push(code);
  }

  return codes;
}

/**
 * `country_list` rows → top-level `DestinationNode[]` (no children attached — provinces are fetched
 * lazily per country). `name`/`enname` are inverted here relative to province/city: live rows show
 * `name` holding the upper-case English label (e.g. `"MALAYSIA"`) and `enname` — despite its own
 * name — holding the Chinese text (e.g. `"马来西亚"`), so the English side is read from `name` and
 * title-cased for display, and the Chinese side from `enname`.
 */
export function normaliseCountries(
  rows: readonly unknown[],
  language: CatalogLanguage,
): readonly DestinationNode[] {
  const nodes: DestinationNode[] = [];

  for (const raw of rows) {
    if (!isRecord(raw)) continue;

    const code = firstNonEmpty(raw, ['id', 'countryid']);
    if (code === undefined) continue;

    const name =
      language === 'zh'
        ? firstNonEmpty(raw, ['enname', 'name'])
        : (() => {
            const english = firstNonEmpty(raw, ['name']);
            return english !== undefined ? toTitleCase(english) : firstNonEmpty(raw, ['enname']);
          })();

    if (name === undefined) continue;

    const min = Number(str(raw, 'min_postcode_digit'));
    const max = Number(str(raw, 'max_postcode_digit'));
    const postcodeDigits =
      Number.isInteger(min) && Number.isInteger(max) && min > 0 && max >= min
        ? { min, max }
        : undefined;

    nodes.push({ code, name, children: [], ...(postcodeDigits ? { postcodeDigits } : {}) });
  }

  return nodes;
}

/**
 * `province_list` rows for a given country → `DestinationNode[]`. Unlike `country_list`, `name` is
 * already the vendor's display string here and `enname` (usually null) is an English fallback.
 */
export function normaliseProvinces(
  rows: readonly unknown[],
  language: CatalogLanguage,
): readonly DestinationNode[] {
  const nodes: DestinationNode[] = [];

  for (const raw of rows) {
    if (!isRecord(raw)) continue;

    const code = firstNonEmpty(raw, ['id', 'provinceid']);
    const name =
      language === 'zh'
        ? firstNonEmpty(raw, ['provincename', 'name'])
        : firstNonEmpty(raw, ['enname', 'provincename', 'name']);

    if (code === undefined || name === undefined) continue;

    nodes.push({ code, name, children: [] });
  }

  return nodes;
}

/** `city_list` rows for a given province → `DestinationNode[]`. Same name/enname shape as province. */
export function normaliseCities(
  rows: readonly unknown[],
  language: CatalogLanguage,
): readonly DestinationNode[] {
  const nodes: DestinationNode[] = [];

  for (const raw of rows) {
    if (!isRecord(raw)) continue;

    const code = firstNonEmpty(raw, ['id', 'cityid']);
    const name =
      language === 'zh'
        ? firstNonEmpty(raw, ['cityname', 'name'])
        : firstNonEmpty(raw, ['enname', 'cityname', 'name']);

    if (code === undefined || name === undefined) continue;

    // Pairs, as the columns come: zip1–zip2 always (534 of 534 live), zip3–zip4 on 16 cities.
    const postcodeRanges: { from: string; to: string }[] = [];
    for (const [fromKey, toKey] of [
      ['zip1', 'zip2'],
      ['zip3', 'zip4'],
    ] as const) {
      const from = str(raw, fromKey);
      const to = str(raw, toKey) ?? from;
      if (from !== undefined && to !== undefined) postcodeRanges.push({ from, to });
    }

    nodes.push({
      code,
      name,
      children: [],
      ...(postcodeRanges.length > 0 ? { postcodeRanges } : {}),
    });
  }

  return nodes;
}

/**
 * Every `province` row (fetched once, with no `countryid`: 70 rows live) grouped by its own
 * `countryid`. One request replaces one per country, and it is also the only way to know which
 * countries have no states at all — see `DestinationNode.available`.
 */
export function provincesByCountry(
  rows: readonly unknown[],
  language: CatalogLanguage,
): ReadonlyMap<string, readonly DestinationNode[]> {
  const grouped = new Map<string, DestinationNode[]>();

  for (const raw of rows) {
    if (!isRecord(raw)) continue;
    const countryCode = firstNonEmpty(raw, ['countryid']);
    if (countryCode === undefined) continue;
    const [node] = normaliseProvinces([raw], language);
    if (node === undefined) continue;
    const list = grouped.get(countryCode) ?? [];
    list.push(node);
    grouped.set(countryCode, list);
  }

  return grouped;
}

/** Marks each country available or not, by whether it has any states. */
export function withCountryAvailability(
  countries: readonly DestinationNode[],
  provinces: ReadonlyMap<string, readonly DestinationNode[]>,
): readonly DestinationNode[] {
  return countries.map((country) => ({
    ...country,
    available: (provinces.get(country.code)?.length ?? 0) > 0,
  }));
}

/** Why a postcode looks wrong for the chosen destination — a warning, never a block. */
export type PostcodeWarning =
  | { readonly kind: 'length'; readonly min: number; readonly max: number }
  | {
      readonly kind: 'range';
      readonly ranges: readonly { readonly from: string; readonly to: string }[];
    };

function inRange(postcode: string, range: { from: string; to: string }): boolean {
  const value = Number(postcode);
  const from = Number(range.from);
  const to = Number(range.to);
  if (!Number.isFinite(value) || !Number.isFinite(from) || !Number.isFinite(to)) return true;
  return value >= Math.min(from, to) && value <= Math.max(from, to);
}

/**
 * Checks a typed postcode against what the vendor records: the country's digit count, then the
 * city's ranges. Only ever a warning — the field is optional and the vendor's ranges may be
 * incomplete — and only for a postcode made of digits: anything else is left alone rather than
 * judged by rules written for numbers. Undefined when there is nothing to say.
 */
export function postcodeWarning(
  postcode: string,
  country: DestinationNode | undefined,
  city: DestinationNode | undefined,
): PostcodeWarning | undefined {
  const typed = postcode.trim();
  if (typed === '' || !/^\d+$/.test(typed)) return undefined;

  const digits = country?.postcodeDigits;
  if (digits && (typed.length < digits.min || typed.length > digits.max)) {
    return { kind: 'length', min: digits.min, max: digits.max };
  }

  const ranges = city?.postcodeRanges;
  if (ranges && ranges.length > 0 && !ranges.some((range) => inRange(typed, range))) {
    return { kind: 'range', ranges };
  }

  return undefined;
}

/**
 * The services one warehouse performs (MYP-89). Every live row carries its own `warehouseid`, and
 * it always matches the key it was listed under, so filtering on the row's own field is enough.
 * An undefined warehouse offers nothing: there is no way to say which services apply.
 */
export function servicesForWarehouse(
  services: readonly OrderService[],
  warehouseId: string | undefined,
): readonly OrderService[] {
  if (warehouseId === undefined) return [];
  return services.filter((service) => service.warehouseId === warehouseId);
}

/**
 * A name for every service id on an order: the catalog's (in the reader's language) where it knows
 * the service, else the order's own `servicename` (retired services, or ones now listed under
 * another warehouse), else the bare id — never nothing.
 */
export function orderServiceNames(
  serviceIds: readonly string[],
  catalog: readonly OrderService[],
  ordered: readonly { readonly id: string; readonly name: string }[] = [],
): readonly { readonly id: string; readonly name: string }[] {
  return serviceIds.map((id) => ({
    id,
    name:
      catalog.find((service) => service.id === id)?.name ??
      ordered.find((service) => service.id === id)?.name ??
      id,
  }));
}
